import { api } from '@/lib/apiClient';
import { env } from '@/lib/env';
import {
  PUSH_SUBSCRIBE_PATH,
  clearPushConfig,
  savePushConfig,
  subscriptionMatchesKey,
  urlBase64ToUint8Array,
} from '@/lib/pushConfig';

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPermissionState(): PushPermissionState {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission as PushPermissionState;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush(): Promise<PushSubscription> {
  console.info('[push] subscribe: starting');
  if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser.');
  if (!env.VITE_VAPID_PUBLIC_KEY) throw new Error('VAPID public key is not configured.');

  const permission = await Notification.requestPermission();
  console.info('[push] subscribe: permission =', permission);
  if (permission !== 'granted') throw new Error('Notification permission was not granted.');

  console.info('[push] subscribe: awaiting service worker…');
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error('Service worker did not register within 10s. Reload the page and try again.')),
        10_000,
      ),
    ),
  ]);
  console.info('[push] subscribe: service worker ready', reg);

  // Stash what the service worker needs to re-subscribe on its own when the
  // browser later rotates the subscription (see the `pushsubscriptionchange`
  // handler in sw.ts). The worker has no access to env or the app stores.
  await savePushConfig({
    vapidPublicKey: env.VITE_VAPID_PUBLIC_KEY,
    baseUrl: env.VITE_VENDOR_BASE_URL,
  });

  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    if (subscriptionMatchesKey(existing, env.VITE_VAPID_PUBLIC_KEY)) {
      console.info('[push] subscribe: using existing subscription');
      await sendSubscriptionToServer(existing);
      return existing;
    }
    // The existing subscription was minted with a different VAPID key (key
    // rotation, or a different environment). It's bound to the old key and
    // silently receives nothing — drop it and mint a fresh one.
    console.info('[push] subscribe: VAPID key changed, replacing stale subscription');
    await existing.unsubscribe();
  }

  console.info('[push] subscribe: creating new subscription');
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
  });
  await sendSubscriptionToServer(sub);
  console.info('[push] subscribe: success');
  return sub;
}

export async function unsubscribeFromPush(): Promise<void> {
  const sub = await getExistingSubscription();
  if (!sub) return;
  try {
    await api.delete(PUSH_SUBSCRIBE_PATH, { data: { endpoint: sub.endpoint } });
  } catch {
    // Swallow: even if server delete fails, unsubscribe locally so the device
    // stops receiving pushes for this user.
  }
  await sub.unsubscribe();
  // Drop the recovery config so the service worker can't re-subscribe an
  // opted-out user, and so the next opt-in starts from a clean slate.
  await clearPushConfig();
}

async function sendSubscriptionToServer(sub: PushSubscription): Promise<void> {
  try {
    const res = await api.post(PUSH_SUBSCRIBE_PATH, {
      ...sub.toJSON(),
      userAgent: navigator.userAgent,
    });
    console.info('[push] server saved subscription', res.data);
  } catch (err) {
    console.error('[push] failed to register subscription with server', err);
    throw err;
  }
}
